import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ToursV3Service } from 'api-v3/services';
import {
    CarrierToursArrayV3,
    SortType,
    ToursFilterProperties,
} from 'api-v3/models';
import {
    setToursLoading,
    setToursError,
    setTours,
} from '@app/stores/toursStore';
import { TourData } from '@app/shared/models/store-types';
import {
    searchState,
    updatePaginationFromApiResponse,
    resetPaginationToEmpty,
} from '@app/stores/searchStore';

@Injectable({
    providedIn: 'root',
})
export class CarrierToursPaginatedService {
    constructor(private toursV3Service: ToursV3Service) {}

    async loadCarrierToursPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        setToursLoading(true);
        setToursError(null);

        try {
            const currentSearchState = searchState();

            // Get selected locations and showClosed filter
            const { getSelectedLocationKeys, getShowClosed } = await import(
                '@app/stores/appStore'
            );
            const selectedSiteKeys = getSelectedLocationKeys('carrier');
            const showClosed = getShowClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            const sortBy = currentSearchState.sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : currentSearchState.sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // Handle uppercase conversion for specific filter properties
            let filterValue = currentSearchState.searchText || undefined;
            const filterProperty =
                currentSearchState.selectedFilterKey?.keyName;

            if (
                filterValue &&
                filterProperty &&
                (filterProperty === 'bundleNumber' ||
                    filterProperty === 'tourNumber')
            ) {
                filterValue = filterValue.toUpperCase();
            }

            const params = {
                organizationSiteKey,
                organizationSiteKeys:
                    selectedSiteKeys.length > 0 ? selectedSiteKeys : undefined,
                limit: currentSearchState.pagination.limit,
                offset: currentSearchState.pagination.offset,
                sort: sortDirection,
                sortBy: validSortBy,
                filterProperty: filterProperty
                    ? (filterProperty as ToursFilterProperties)
                    : undefined,
                filterValue: filterValue,
                filterNotClosed: !showClosed,
            };

            // This will go through the HTTP interceptors
            const response = await lastValueFrom(
                this.toursV3Service.getCarrierToursV3(params)
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const toursWithOrgSiteKey: TourData[] = response.data.map(
                    tour => ({
                        ...tour,
                        orgSiteKey: organizationSiteKey,
                    })
                );

                setTours(toursWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_TOURS|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CARRIER_TOURS';

            setToursError(errorMessage);

            // Reset pagination to empty state on error
            resetPaginationToEmpty();
        } finally {
            setToursLoading(false);
        }
    }
}
